import React from "react";
//#region redux
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "store";
import {
  clickedCreateSupportCase,
  receivedRetrievedFAQArticles,
  wroteQuestion,
  sentSupportCase,
} from "store/actions/diagnosticsActions";
//#endregion
//#region types
import { IconDefinitionConstants, FAQConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { FAQPopperContentProps } from "models/propTypes/supportIssuePropTypes";
import { DisplayType } from "models/enums/style";
//#endregion
//#region components
import { ActionLinkButton, SubmitInput, LighterTitle, CircularLoadingWithText, Icon } from "components/common";
import { Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import FAQArticleLink from "./faqArticleLink";
import ContactSupportSection from "./contactSupportSection";
import SupportTicketCreateFormPopper from "../createFormPopper";
import { AgentStep } from "../agent";
import useCurrentAgentConversation from "../agent/useCurrentAgentConversation";
import useAgentPopperTracking, { popperStepOf } from "../agent/useAgentPopperTracking";
import { useAgentAccess } from "../agent/agentAccess";
import { modeOf } from "../agent/agentFlow";
import useHeightTransition from "../agent/useHeightTransition";
import { isAgentUnavailable, markAgentUnavailable } from "utils/helpers/agentConversationStore";
import { useSupportService } from "serviceHooks";
import { useSupportTeamContent } from "serviceHooks/useSupportTeamContent";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";

const FAQPopperContent: React.FC<FAQPopperContentProps> = (props) => {
  const supportService = useSupportService();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [comment, setComment] = React.useState<string>();
  const { conversation: agentConversation, state: agentState } = useCurrentAgentConversation();
  const [showSupportForm, setShowSupportForm] = React.useState<boolean>(false);
  const [suggestedArticles, setSuggestedArticles] = React.useState<WintSuperkollAPIModel.FAQItem[]>();
  const [searchError, setSearchError] = React.useState<boolean>(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const containerRef = React.useRef<HTMLDivElement>(null);

  const companyId = useSelector((state: AppState) => state.auth.selectedCompany.id);
  const { data: supportTeamData } = useSupportTeamContent(companyId);
  const skipFaqStep = supportTeamData?.team?.skipFaqStep ?? false;
  const showAllLink = FAQConstants.FAQUrl;

  // The agent step, before the FAQ step, for the companies that have it (see agentAccess).
  const agentEnabled = useAgentAccess();
  const [agentUnavailable, setAgentUnavailable] = React.useState<boolean>(isAgentUnavailable);
  const showAgentStep = agentEnabled && !agentUnavailable;

  const agentMode = modeOf(agentState);
  // The team's own rule decides who may call (`showPhone`); an agency's customers call their agency, not Wint.
  const canCall =
    !!supportTeamData &&
    !supportTeamData.externalAgencyId &&
    supportTeamData.showPhone &&
    !!supportTeamData.content?.phoneNumber;
  useAgentPopperTracking(
    agentConversation,
    agentEnabled,
    popperStepOf(agentMode, agentUnavailable),
    agentState.tracking.conversationId
  );
  // The popper is anchored at the bottom, so the form turning into the thread grows it upward.
  useHeightTransition(containerRef, showAgentStep ? agentMode : undefined);

  const onOpenCase = (caseId: number) => {
    agentConversation.track("case.opened", { caseId });
    props.closePopper && props.closePopper();
    navigate(UrlHelper.formatPath(`/Support/${caseId}`));
  };

  const defaultArticles: WintSuperkollAPIModel.FAQItem[] = [
    {
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.FAQHelpItem1Title),
      answer: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.FAQHelpItem1Text),
      url: `${FAQConstants.FAQUrl}/${FAQConstants.FAQPageUrl}/${FAQConstants.Article1Url}`,
    },
    {
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.FAQHelpItem2Title),
      answer: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.FAQHelpItem2Text),
      url: `${FAQConstants.FAQUrl}/${FAQConstants.FAQPageUrl}/${FAQConstants.Article2Url}`,
    },
    {
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.FAQHelpItem3Title),
      answer: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.FAQHelpItem3Text),
      url: `${FAQConstants.FAQUrl}/${FAQConstants.FAQPageUrl}/${FAQConstants.Article3Url}`,
    },
  ];

  const handleSubmit = (text: string) => {
    setLoading(true);
    setSearchError(false);
    setComment(text);
    props.setSubmitMessage && props.setSubmitMessage(text);
    dispatch(wroteQuestion(text));

    return supportService
      .getFAQSuggestions(text, 3)
      .then((result) => {
        setSuggestedArticles(result.items ?? []);
        const faqIds = result.items?.map((f) => String(f.id)) ?? [];
        dispatch(receivedRetrievedFAQArticles(faqIds));
      })
      .catch(() => {
        setSuggestedArticles(undefined);
        setSearchError(true);
      })
      .finally(() => setLoading(false));
  };

  const setIsSupportStep = (isSupportStep: boolean) => {
    setShowSupportForm(isSupportStep);
    props.setIsSupportStep && props.setIsSupportStep(isSupportStep);
  };

  const onCreateSupportCaseClick = () => {
    dispatch(clickedCreateSupportCase());
    setIsSupportStep(true);
  };

  // The server says the agent is off: show the FAQ step as if it had just opened, without
  // searching for the question she wrote to the agent.
  const onAgentUnavailable = () => {
    agentConversation.noteTracking({ agentUnavailable: true });
    agentConversation.track("agent.unavailable");
    markAgentUnavailable();
    setAgentUnavailable(true);
  };

  if (showSupportForm) {
    return (
      <SupportTicketCreateFormPopper
        closePopper={props.closePopper}
        defaultMessage={comment}
        onSubmit={() => dispatch(sentSupportCase())}
        onFormUpdate={props.onSupportFormUpdate}
      />
    );
  }

  const articles = suggestedArticles ?? defaultArticles;
  const showNoResults = suggestedArticles !== undefined && suggestedArticles.length === 0;

  return (
    <div className={cssExports.faqPopperContainer} ref={containerRef}>
      <div className={cssExports.faqPopperTop}>
        {showAgentStep ? (
          <Grid container className={cssExports.agentPopperHeader}>
            <LighterTitle
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.ContactSupport)}
            />
          </Grid>
        ) : (
          <LighterTitle
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.FAQPopperTitle)}
            subTitle={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.FAQPopperSubtitle)}
          />
        )}

        {showAgentStep ? (
          <AgentStep onUnavailable={onAgentUnavailable} onOpenCase={onOpenCase} />
        ) : (
          !skipFaqStep && (
            <>
              <div id="commentInput">
                <SubmitInput
                  label={LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.SupportIssue.FAQPopperSubmitTextLabel
                  )}
                  icon={IconDefinitionConstants.WSearch}
                  rootClass={cssExports.addComment}
                  buttonIcon={IconDefinitionConstants.WSearch}
                  buttonClass={cssExports.commentsInputButton}
                  buttonText={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Submit)}
                  handleSubmit={handleSubmit}
                  submitOnEnter
                  floatingLabel
                  keepValueOnSubmit
                />
              </div>

              <div className={cssExports.faqSuggestions}>
                {!loading && !searchError && (
                  <Grid
                    container
                    className={cssExports.faqListHeader}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <span className={cssExports.faqListHeaderTitle}>
                      {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.FAQCommonQuestions)}
                    </span>
                    <ActionLinkButton
                      className={cssExports.faqShowAllLink}
                      onClick={() => window.open(showAllLink, "_blank", "noopener,noreferrer")}
                      endIcon={<Icon icon={IconDefinitionConstants.WArrowSmallRight} displayType={DisplayType.Info} />}
                    >
                      {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.FAQShowAll)}
                    </ActionLinkButton>
                  </Grid>
                )}

                <Grid container direction="column" rowGap={0.5}>
                  {loading ? (
                    <CircularLoadingWithText
                      loadingText={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Searching)}
                    />
                  ) : searchError ? (
                    <div className={cssExports.faqError}>
                      <div className={cssExports.faqErrorTitle}>
                        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.FAQErrorTitle)}
                      </div>
                      <div className={cssExports.faqErrorBody}>
                        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.FAQErrorBody)}
                      </div>
                      <ActionLinkButton
                        className={cssExports.faqErrorLink}
                        onClick={() => window.open(FAQConstants.FAQUrl, "_blank", "noopener,noreferrer")}
                        endIcon={
                          <Icon icon={IconDefinitionConstants.WArrowSmallRight} displayType={DisplayType.Info} />
                        }
                      >
                        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.FAQBrowseFAQ)}
                      </ActionLinkButton>
                    </div>
                  ) : showNoResults ? (
                    <div className={cssExports.faqError}>
                      <div className={cssExports.faqErrorTitle}>
                        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.FAQNoResultTitle)}
                      </div>
                      <div className={cssExports.faqErrorBody}>
                        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.FAQNoResultBody)}
                      </div>
                    </div>
                  ) : (
                    articles.map((article, index) => <FAQArticleLink key={article.id ?? index} article={article} />)
                  )}
                </Grid>
              </div>
            </>
          )
        )}
      </div>

      {!showAgentStep ? (
        <ContactSupportSection data={supportTeamData} onCreateCase={onCreateSupportCaseClick} />
      ) : agentMode === "ask" && canCall ? (
        <ContactSupportSection
          data={supportTeamData}
          onCreateCase={onCreateSupportCaseClick}
          title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.AgentCallInstead)}
          hideCreateCase
        />
      ) : null}
    </div>
  );
};

export default FAQPopperContent;
